/** Manages all active enemies: AI updates, separation, alert propagation, corpses, and wiring callbacks. */
import Phaser from 'phaser';
import { Enemy } from '../entities/Enemy';
import { ConfigLoader } from './ConfigLoader';
import type { SectorConfig } from './ConfigLoader';
import type { PlayerHideState } from '../utils/Types';
import { distance, distanceSq, normalize } from '../utils/Math';
import { ALERT_PROPAGATION_INTERVAL_MS, ALERT_RELAY_ATTENUATION, ALERT_AGGRO_FLOOR } from '../ai/constants';
import { DebugLog } from '../debug/DebugLog';
import { EnemySpawner } from './EnemySpawner';
import type { SpawnerCallbacks } from './EnemySpawner';
import type { StatusEffectSystem } from './StatusEffectSystem';

/** A corpse position recorded at death time, with an expiration timestamp. */
export interface CorpseRecord {
  x: number;
  y: number;
  expiresAt: number;
  sprite?: Phaser.GameObjects.Sprite;
}

/** Creates and manages all active enemies, handling AI updates, separation, alert propagation, and corpse tracking. */
export class EnemyManager {
  private scene: Phaser.Scene;
  private enemies: Enemy[] = [];
  private spawner: EnemySpawner;

  private _playerSprite: Phaser.GameObjects.GameObject | null = null;
  private _projectileSpawner: ((x: number, y: number, tx: number, ty: number, cfg: { speed: number; damage: number; lifetimeMs: number; size: number; color: number; attackType: string }) => void) | null = null;
  private _backupCallback: ((originX: number, originY: number, radius: number) => void) | null = null;
  private _corpseProvider: (() => readonly CorpseRecord[]) | null = null;
  private _buildingBuffers: Phaser.Physics.Arcade.StaticGroup | null = null;
  private _statusEffectSystem: StatusEffectSystem | null = null;
  private _isStealthCorpseVanish: (() => boolean) | null = null;

  // Frame counter for off-screen AI throttling
  private frameCount = 0;

  // Enemies whose HP reached zero — queued for removal at next sweep
  private pendingDeath: Set<Enemy> = new Set();

  // Corpse positions for AI investigation
  private corpses: CorpseRecord[] = [];

  // Alert propagation sweep timer
  private _alertPropagationTimer: number = 0;

  constructor(scene: Phaser.Scene, groundLayer: Phaser.Tilemaps.TilemapLayer) {
    this.scene = scene;

    // Build callback bag that the spawner uses to wire each new enemy
    const callbacks: SpawnerCallbacks = {
      get playerSprite() { return self._playerSprite; },
      get buildingBuffers() { return self._buildingBuffers; },
      get projectileSpawner() { return self._projectileSpawner; },
      get backupCallback() { return self._backupCallback; },
      get corpseProvider() { return self._corpseProvider; },
      get statusEffectSystem() { return self._statusEffectSystem; },
      onEnemyDied: (enemy: Enemy) => { this.pendingDeath.add(enemy); },
    };
    const self = this;

    this.spawner = new EnemySpawner(scene, groundLayer, this.enemies, callbacks);
  }

  get all(): readonly Enemy[] {
    return this.enemies;
  }

  /** Wire the status effect system so newly-spawned enemies get per-entity trackers. Backfills any existing enemies. */
  setStatusEffectSystem(system: StatusEffectSystem): void {
    this._statusEffectSystem = system;
    for (const enemy of this.enemies) {
      if (!enemy.statusEffectTracker) {
        enemy.statusEffectTracker = system.createTracker();
      }
    }
  }

  // --- Spawn delegation ---

  /** Spawn a single enemy of the given archetype at the nearest walkable tile. */
  spawnAt(x: number, y: number, archetypeId: string): Enemy | null {
    return this.spawner.spawnAt(x, y, archetypeId);
  }

  /** Spawn the initial enemy population from a sector config. */
  populateFromSector(sectorConfig: SectorConfig): void {
    this.spawner.populateFromSector(sectorConfig);
  }

  /** Spawn the default MVP enemy layout near the player's current position. */
  spawnNearPlayer(playerX: number, playerY: number): void {
    this.spawner.spawnNearPlayer(playerX, playerY);
  }

  // --- Update ---

  /** Tick all enemy AI, update logic, enforce separation, and process respawn timers for the current frame. */
  update(playerX: number, playerY: number, delta: number, playerHideState?: PlayerHideState): void {
    const cam = this.scene.cameras.main;
    const view = cam.worldView;
    this.frameCount++;

    for (let i = 0; i < this.enemies.length; i++) {
      const enemy = this.enemies[i];

      // Off-screen duty enemies: throttle AI to every 10th frame (staggered by index)
      const onScreen = view.contains(enemy.sprite.x, enemy.sprite.y);
      const skipAI = !onScreen && enemy.ai.state === 'duty'
        && (this.frameCount + i) % 10 !== 0;

      if (!skipAI) {
        const aiDelta = enemy.throttledDelta + delta;
        enemy.throttledDelta = 0;
        enemy.updateAI(playerX, playerY, aiDelta, playerHideState);
      } else {
        enemy.throttledDelta += delta;
      }
      enemy.update(delta);
    }
    this.applySeparation();
    this.propagateAlertStates(delta);
    this.spawner.processRespawns();
    this.pruneCorpses();
  }

  /** Push overlapping enemies apart so they don't pile up on the same tile.
   *  Per-archetype separationRadius/separationForce override the global physics defaults. */
  private applySeparation(): void {
    const globalSep = ConfigLoader.physics.separation;
    const enemies = this.enemies;
    const len = enemies.length;
    for (let i = 0; i < len; i++) {
      const a = enemies[i];
      if (a.isKnockedBack) continue;
      const ax = a.sprite.x;
      const ay = a.sprite.y;
      const aRadius = a.config.separationRadius ?? globalSep.radius;
      const aForce = a.config.separationForce ?? globalSep.force;
      for (let j = i + 1; j < len; j++) {
        const b = enemies[j];
        if (b.isKnockedBack) continue;
        const bRadius = b.config.separationRadius ?? globalSep.radius;
        const bForce = b.config.separationForce ?? globalSep.force;

        // Use the larger radius so the "bigger personal space" always triggers
        const radius = Math.max(aRadius, bRadius);

        const dx = b.sprite.x - ax;
        const dy = b.sprite.y - ay;
        const dSq = distanceSq(ax, ay, b.sprite.x, b.sprite.y);
        if (dSq >= radius * radius || dSq === 0) continue;

        const dist = Math.sqrt(dSq);
        const overlapRatio = (radius - dist) / radius;
        const n = normalize(dx, dy);
        const nx = n.x;
        const ny = n.y;

        a.body.velocity.x -= nx * aForce * overlapRatio;
        a.body.velocity.y -= ny * aForce * overlapRatio;
        b.body.velocity.x += nx * bForce * overlapRatio;
        b.body.velocity.y += ny * bForce * overlapRatio;
      }
    }
  }

  /** Push nearby enemies away from a point (e.g. player corpse). Enemies within radius get a velocity impulse and skip AI for the duration. */
  retreatFromPoint(x: number, y: number, radius: number, speed: number, durationMs: number): void {
    const now = this.scene.time.now;
    for (const enemy of this.enemies) {
      const dSq = distanceSq(x, y, enemy.sprite.x, enemy.sprite.y);
      if (dSq >= radius * radius || dSq === 0) continue;

      const n = normalize(enemy.sprite.x - x, enemy.sprite.y - y);
      enemy.body.setVelocity(n.x * speed, n.y * speed);
      enemy.retreatUntil = now + durationMs;
    }
  }

  /**
   * Remove enemies marked for death by the entity-died event.
   * Called after all damage sources have resolved (knockback, combat, projectiles)
   * so the entity is never destroyed while another system still references it.
   */
  sweepDeadEnemies(): void {
    if (this.pendingDeath.size === 0) return;
    const now = Date.now();
    const vanish = this._isStealthCorpseVanish?.() ?? false;
    for (const enemy of this.pendingDeath) {
      if (this.enemies.includes(enemy)) {
        const cx = enemy.sprite.x;
        const cy = enemy.sprite.y;
        const { duration, corpseSprite } = enemy.playDeath(() => this.removeEnemy(enemy, true));
        if (vanish) {
          // Stealth kill — no corpse evidence. Destroy the sprite immediately; skip the record.
          corpseSprite?.destroy();
          DebugLog.log('combat', `CORPSE VANISH at (${Math.round(cx)},${Math.round(cy)})`);
        } else {
          this.corpses.push({ x: cx, y: cy, expiresAt: now + duration, sprite: corpseSprite ?? undefined });
        }
      }
    }
    this.pendingDeath.clear();
  }

  /** Return true if any active enemy currently has line-of-sight to the player. */
  anyEnemyHasLOS(): boolean {
    return this.enemies.some(e => e.ai.hasLOS);
  }

  /** Wipe aggro on all enemies within the given radius of a world position. */
  wipeAggroInRadius(x: number, y: number, radius: number): void {
    const rSq = radius * radius;
    for (const enemy of this.enemies) {
      if (enemy.healthComponent.isDead) continue;
      if (distanceSq(enemy.sprite.x, enemy.sprite.y, x, y) <= rSq) {
        enemy.ai.wipeAggro();
      }
    }
  }

  /** Remove and destroy a specific enemy from the active roster. Optionally queue for respawn. */
  removeEnemy(enemy: Enemy, queueForRespawn = false): void {
    const idx = this.enemies.indexOf(enemy);
    if (idx !== -1) {
      if (queueForRespawn) {
        const duty = enemy.ai.dutyPosition;
        const meta = enemy.respawnMeta;
        this.spawner.queueRespawn(enemy.config.id, duty.x, duty.y, meta?.respawnX, meta?.respawnY);
      }
      this.enemies.splice(idx, 1);
      enemy.destroy();
    }
  }

  /** Register the player sprite and wire physics colliders between it and all existing enemies. */
  setupColliders(playerSprite: Phaser.GameObjects.GameObject): void {
    this._playerSprite = playerSprite;

    // Wire player-enemy colliders for enemies already spawned before setupColliders was called
    for (const enemy of this.enemies) {
      this.scene.physics.add.collider(playerSprite, enemy.sprite);
    }
  }

  /** Set building wall buffers for enemy collision. Applied retroactively and automatically to future spawns. */
  setBuildingBuffers(group: Phaser.Physics.Arcade.StaticGroup): void {
    this._buildingBuffers = group;
    for (const enemy of this.enemies) {
      this.scene.physics.add.collider(enemy.sprite, group);
    }
  }

  /** Set the backup alert callback. Applied retroactively and automatically to future spawns. */
  setBackupCallback(callback: (originX: number, originY: number, radius: number) => void): void {
    this._backupCallback = callback;
    for (const enemy of this.enemies) {
      enemy.setBackupCallback(callback);
    }
  }

  /** Set the corpse provider callback. Applied retroactively to existing enemies and automatically to future spawns. */
  setCorpseProvider(provider: () => readonly CorpseRecord[]): void {
    this._corpseProvider = provider;
    for (const enemy of this.enemies) {
      enemy.ai.setCorpseProvider(provider);
    }
  }

  /** Set the projectile spawn callback. Applied retroactively to existing enemies and automatically to future spawns. */
  setProjectileSpawner(spawner: (x: number, y: number, tx: number, ty: number, cfg: { speed: number; damage: number; lifetimeMs: number; size: number; color: number; attackType: string }) => void): void {
    this._projectileSpawner = spawner;
    for (const enemy of this.enemies) {
      enemy.setProjectileSpawner(spawner);
    }
  }

  /** Set the stealth corpse vanish check. When true, kills from stealth skip corpse creation. */
  setStealthCorpseVanish(check: () => boolean): void {
    this._isStealthCorpseVanish = check;
  }

  /** Sweep for enemy-to-enemy alert propagation: duty enemies that perceive an agitated ally go alert. */
  private propagateAlertStates(delta: number): void {
    this._alertPropagationTimer -= delta;
    if (this._alertPropagationTimer > 0) return;
    this._alertPropagationTimer = ALERT_PROPAGATION_INTERVAL_MS;

    const noisy: Enemy[] = [];
    const receivers: Enemy[] = [];
    for (const enemy of this.enemies) {
      const state = enemy.ai.state;
      if (state === 'chase' || state === 'search' || state === 'using_skill') {
        noisy.push(enemy);
      } else if (state === 'alert' && enemy.ai.aggro > ALERT_AGGRO_FLOOR) {
        noisy.push(enemy);
      }
      if (state === 'duty') {
        receivers.push(enemy);
      }
    }

    if (noisy.length === 0 || receivers.length === 0) return;

    let hitCount = 0;
    for (const receiver of receivers) {
      for (const source of noisy) {
        if (receiver.ai.canPerceiveAlertedEnemy(
          receiver.sprite.x, receiver.sprite.y,
          source.sprite.x, source.sprite.y,
        )) {
          receiver.ai.alertFromAlly(source.ai.aggro * ALERT_RELAY_ATTENUATION);
          hitCount++;
          break; // one alert per sweep per enemy
        }
      }
    }

    if (hitCount > 0) {
      DebugLog.throttled('alert-propagation', 2000, 'ai',
        `ALERT PROPAGATION: ${hitCount} enemies alerted by allies`);
    }
  }

  /** Clear the spawner's pending respawn queue without destroying managed enemies. */
  clearRespawnQueue(): void {
    this.spawner.clearQueue();
  }

  /** Clear the pending-death set so queued deaths don't fire during the next frame. */
  clearPendingDeaths(): void {
    this.pendingDeath.clear();
  }

  /** Destroy all corpse sprites and clear corpse records. */
  clearCorpses(): void {
    for (const c of this.corpses) {
      if (c.sprite && !c.sprite.scene) continue; // already destroyed
      c.sprite?.destroy();
    }
    this.corpses = [];
  }

  /** Remove expired corpse records. */
  private pruneCorpses(): void {
    if (this.corpses.length === 0) return;
    const now = Date.now();
    this.corpses = this.corpses.filter(c => c.expiresAt > now);
  }

  /** Active corpse positions for AI investigation. */
  get activeCorpses(): readonly CorpseRecord[] {
    return this.corpses;
  }

  /** Bump aggro on all enemies within radius of a combat sound (no LOS required). */
  broadcastCombatAlert(x: number, y: number, amount: number, radius: number): void {
    let hitCount = 0;
    for (const enemy of this.enemies) {
      const dist = distance(x, y, enemy.sprite.x, enemy.sprite.y);
      if (dist <= radius && dist > 0) {
        enemy.ai.raiseAlert(amount);
        hitCount++;
      }
    }
    if (hitCount > 0) {
      DebugLog.log('combat', `COMBAT ALERT at (${Math.round(x)},${Math.round(y)}) → ${hitCount} enemies within ${radius}px`);
    }
  }

  /** Destroy all enemies and clear the roster. */
  destroy(): void {
    for (const enemy of this.enemies) {
      enemy.destroy();
    }
    this.enemies = [];
    this.spawner.clearQueue();
    this.corpses = [];
  }
}
